"use client"

import type { components } from "umbraco"
import { getNonUmbracoUrl } from "utils"
import Container from "../../Container"
import {
  Root,
  PageTitle,
  IntroText,
  SectionsGrid,
  Section,
  SectionTitle,
  LinkList,
  SitemapLink,
} from "./styles"

type HtmlSitemapData =
  components["schemas"]["HtmlSitemapBlockPropertiesModel"]

type SitemapSectionItemProps =
  components["schemas"]["SitemapSectionItemPropertiesModel"]

type ApiLink = components["schemas"]["ApiLinkModel"]

type ApiBlockList = components["schemas"]["ApiBlockListModel"]

type SitemapManualLinkProps =
  components["schemas"]["SitemapManualLinkPropertiesModel"]

interface AutoSectionItem {
  title: string
  href: string
  children: Array<{ title: string; href: string }>
}

interface SitemapPageMap {
  [path: string]: {
    title: string
    href: string
    children: Array<{ title: string; href: string }>
    isContainer?: boolean
  }
}

interface Props {
  data: HtmlSitemapData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  autoSections?: AutoSectionItem[]
  sitemapPageMap?: SitemapPageMap
  hiddenSitemapPaths?: string[]
  [key: string]: unknown
}

interface RenderedLink {
  title: string
  href: string
  target?: string | null
}

interface RenderedSection {
  title: string
  href: string
  children: RenderedLink[]
}

function getLinkHref(link: ApiLink): string {
  return link.route?.path ?? link.url ?? ""
}

function sortLinks(
  links: RenderedLink[],
  sortMode: string | null | undefined,
  culture: string,
): RenderedLink[] {
  if (sortMode === "alphabetical") {
    return [...links].sort((a, b) => a.title.localeCompare(b.title, culture))
  }
  return links
}

// EGDE-328: manual links now carry a MANDATORY title via the `manualLinks`
// element (Block List). Each block has `linkTitle` (required) + `link`.
function buildManualLinkBlocks(
  blockList: ApiBlockList | undefined,
): RenderedLink[] {
  const out: RenderedLink[] = []
  for (const item of blockList?.items ?? []) {
    const props = item.content?.properties as SitemapManualLinkProps | undefined
    const link = (props?.link ?? [])[0]
    if (!props?.linkTitle || !link) continue
    const href = getLinkHref(link)
    if (!href) continue
    out.push({ title: props.linkTitle, href, target: link.target })
  }
  return out
}


function resolveDropdown(
  value: string | string[] | null | undefined,
): string | null {
  const v = Array.isArray(value) ? value[0] : value
  return v ?? null
}

// Listing/container content types whose own route is not a real destination
// (it resolves to the homepage). Sections rooted at these should list the
// container's children rather than linking to the container itself.
const CONTAINER_CONTENT_TYPES = new Set(["pages"])

function isContainerNode(rootRecord: Record<string, unknown>): boolean {
  const contentType = rootRecord.contentType as string | undefined
  return contentType ? CONTAINER_CONTENT_TYPES.has(contentType) : false
}

// EGDE-589: compare destinations, not labels — the same page can legitimately
// appear under different titles (a section alias vs. the page name), and two
// different pages can legitimately share a name (e.g. several "נהג אוטובוס"
// career posts).
function sameDestination(a: string, b: string): boolean {
  if (!a || !b) return false
  const strip = (s: string) => (s.length > 1 ? s.replace(/\/+$/, "") : s)
  return strip(a) === strip(b)
}

// EGDE-589: a section must never list one destination twice. Keeps the first
// occurrence so authored order (and the auto-before-manual order) is preserved.
function dedupeByHref(links: RenderedLink[]): RenderedLink[] {
  const seen = new Set<string>()
  const out: RenderedLink[] = []
  for (const link of links) {
    // An empty href is not a destination — several children that failed to
    // resolve a route would otherwise collapse into one, silently dropping
    // entries instead of deduplicating them. Pass them through untouched.
    if (!link.href) {
      out.push(link)
      continue
    }
    const key = link.href.length > 1 ? link.href.replace(/\/+$/, "") : link.href
    if (seen.has(key)) continue
    seen.add(key)
    out.push(link)
  }
  return out
}

// EGDE-590: "Hide from Sitemap" is applied by the loader while it walks the page
// tree, but a CMS-authored section resolves its OWN Root Nodes from the block
// value — that path never passed through the filter, so a hidden page still
// appeared, as the section heading's link. The loader now also reports every
// hidden destination; normalise the same way sameDestination/dedupeByHref do so a
// trailing slash can't smuggle one past.
function makeHiddenCheck(hiddenPaths: string[]): (href: string) => boolean {
  const strip = (s: string) => (s.length > 1 ? s.replace(/\/+$/, "") : s)
  const hidden = new Set(hiddenPaths.filter(Boolean).map(strip))
  if (hidden.size === 0) return () => false
  return (href: string) => Boolean(href) && hidden.has(strip(href))
}

function buildSectionFromCms(
  sectionProps: SitemapSectionItemProps,
  pageMap: SitemapPageMap,
  culture: string,
  isHiddenHref: (href: string) => boolean,
): RenderedSection | null {
  const sourceMode = resolveDropdown(sectionProps.sourceMode)
  const sortMode = resolveDropdown(sectionProps.sortMode)
  const title = sectionProps.sectionTitle ?? ""
  const depth = sectionProps.depth ?? 2
  const maxItems = sectionProps.maxItems

  let children: RenderedLink[] = []

  // The section title links to its first root node only when that node is a
  // real page. Container root nodes (e.g. "pages") have no real destination —
  // linking to "/pages/" resolves to the homepage — so the title stays plain.
  // EGDE-589: resolved up-front because the auto pass below must know whether
  // the heading already points at a root node, or that root gets listed twice.
  const firstRoot = (sectionProps.rootNodes ?? [])[0] as
    | Record<string, unknown>
    | undefined
  const firstRootPath =
    ((firstRoot?.route as Record<string, unknown>)?.path as string) ?? ""
  const firstRootMapped = pageMap[firstRootPath]
  const firstRootIsContainer =
    firstRootMapped?.isContainer === true ||
    (firstRoot ? isContainerNode(firstRoot) : false)
  // EGDE-590: a hidden root leaves the heading as plain text instead of a link to
  // the page the editor asked to keep out of the sitemap. The section itself
  // stays and still lists that page's subpages — the flag hides one page, not a
  // whole branch (EGDE-590 r2, QA).
  const href =
    firstRootIsContainer || isHiddenHref(firstRootPath) ? "" : firstRootPath

  if (sourceMode === "auto" || sourceMode === "mixed") {
    const rootNodes = sectionProps.rootNodes ?? []
    for (const rootNode of rootNodes) {
      const rootRecord = rootNode as Record<string, unknown>
      const routePath =
        (rootRecord.route as Record<string, unknown>)?.path as string ?? ""
      const rootTitle = (rootRecord.name as string) ?? ""
      const mapped = pageMap[routePath]

      const isContainer =
        mapped?.isContainer === true || isContainerNode(rootRecord)

      if (isContainer) {
        // The root node is a listing container (e.g. the "pages" container,
        // route "/pages/"). Its own route resolves to the homepage, so we must
        // NOT emit it as a link. Instead list its children — the main pages.
        if (mapped) {
          children = [...children, ...mapped.children]
        }
      } else {
        // The root node is a real leaf page: list it, plus its own children
        // when depth allows.
        // EGDE-589: skip it when the section heading is already a link to this
        // exact page — otherwise the same URL is emitted twice, once under the
        // authored section title and once under the page's own name.
        // EGDE-590: and it is not listed as one of the section's links either.
        const headingOwnsRoot = sameDestination(href, routePath)
        const rootIsListable =
          Boolean(rootTitle) &&
          Boolean(routePath) &&
          !isHiddenHref(routePath) &&
          !headingOwnsRoot
        if (rootIsListable) {
          children = [
            ...children,
            { title: mapped?.title ?? rootTitle, href: routePath },
          ]
        }
        // `depth` counts the levels this section LISTS, with the root as the
        // first. A root the section cannot list — because the heading already
        // links to it (EGDE-589) or because it is hidden (EGDE-590) — does not
        // consume that level, so the level the editor asked for is the one below
        // it. Without this, removing the duplicate link left a depth-1 section
        // as a bare heading with nothing under it (EGDE-589 r2, QA).
        if (mapped && (depth > 1 || !rootIsListable)) {
          children = [...children, ...mapped.children]
        }
      }
    }
  }

  if (sourceMode === "manual" || sourceMode === "mixed") {
    // EGDE-328: manual links come from the `manualLinks` block list, whose
    // element enforces a mandatory title.
    children = [...children, ...buildManualLinkBlocks(sectionProps.manualLinks)]
  }

  // EGDE-590: a hidden page must not reach the list by any route — including a
  // manual link an editor left behind after flagging the page as hidden.
  children = children.filter((child) => !isHiddenHref(child.href))

  // EGDE-589: dedupe before sorting/limiting so a page repeated across root
  // nodes or duplicated by a manual link cannot consume a maxItems slot.
  children = dedupeByHref(children)

  children = sortLinks(children, sortMode, culture)

  if (maxItems && maxItems > 0) {
    children = children.slice(0, maxItems)
  }

  if (!title) return null

  // EGDE-590: with no destination AND no links left the section would render as
  // dead text, so it is dropped. A hidden root that still has subpages does not
  // reach here — it keeps the heading and lists them (EGDE-590 r2).
  if (!href && children.length === 0) return null

  return { title, href, children }
}

function SitemapSection({ section }: { section: RenderedSection }) {
  return (
    <Section>
      <SectionTitle variant="h3">
        {section.href ? (
          <SitemapLink href={getNonUmbracoUrl(section.href)}>
            {section.title}
          </SitemapLink>
        ) : (
          section.title
        )}
      </SectionTitle>

      {section.children.length > 0 && (
        <LinkList>
          {section.children.map((child, idx) => (
            <SitemapLink
              key={`link-${idx}`}
              href={getNonUmbracoUrl(child.href)}
              {...(child.target ? { target: child.target, rel: "noopener noreferrer" } : {})}
            >
              {child.title}
            </SitemapLink>
          ))}
        </LinkList>
      )}
    </Section>
  )
}

export default function HtmlSitemap({
  data,
  autoSections,
  sitemapPageMap,
  hiddenSitemapPaths,
  culture,
}: Props) {
  const cmsSections = data.sections?.items ?? []
  const pageMap = sitemapPageMap ?? {}
  const isHiddenHref = makeHiddenCheck(hiddenSitemapPaths ?? [])

  let sections: RenderedSection[] = []

  if (cmsSections.length > 0) {
    sections = cmsSections
      .map((item) => {
        const props = item.content.properties as
          | SitemapSectionItemProps
          | undefined
        if (!props) return null
        return buildSectionFromCms(props, pageMap, culture, isHiddenHref)
      })
      .filter((s): s is RenderedSection => s !== null)
  } else if (autoSections && autoSections.length > 0) {
    // Fallback to auto-discovered sections from page tree
    sections = autoSections
  }

  return (
    <Root data-block-type="html-sitemap">
      <Container paddingMobile>
        {data.title && <PageTitle variant="h1" component="h2">{data.title}</PageTitle>}

        {data.introText && <IntroText>{data.introText}</IntroText>}

        <SectionsGrid>
          {sections.map((section, index) => (
            <SitemapSection key={`section-${index}`} section={section} />
          ))}
        </SectionsGrid>
      </Container>
    </Root>
  )
}
